import React, { useEffect, useMemo, useState } from 'react';
import api from '../services/api';
import PageHero from '../components/layout/PageHero';

type FundingSource = {
  _id: string;
  title: string;
  category: 'ongoing' | 'completed';
  sponsor?: string;
  period?: string;
  amount?: string;
  description?: string;
  order?: number;
};

const renderHtml = (html?: string) => ({ __html: html || '' });

const FundingSources: React.FC = () => {
  const [items, setItems] = useState<FundingSource[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        const res = await api.get<FundingSource[]>('/funding-sources');
        setItems(res.data || []);
      } catch (err) {
        console.error(err);
        setError('Failed to load funding sources. Please try again later.');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const ongoingProjects = useMemo(
    () => items.filter((item) => item.category === 'ongoing'),
    [items]
  );
  const completedProjects = useMemo(
    () => items.filter((item) => item.category === 'completed'),
    [items]
  );

  const renderList = (list: FundingSource[]) => {
    if (list.length === 0) {
      return (
        <p className="rounded-md bg-surface-container-low px-4 py-3 text-sm text-on-surface-variant">
          No project records yet.
        </p>
      );
    }

    return (
      <div className="space-y-4">
        {list.map((item) => (
          <article
            key={item._id}
            className="um-card bg-surface-container-lowest p-6 transition-colors hover:bg-surface-container-low"
          >
            <h3
              className="text-2xl font-semibold text-on-surface"
              dangerouslySetInnerHTML={renderHtml(item.title)}
            />
            <div className="mt-3 flex flex-wrap gap-x-6 gap-y-1 text-sm text-on-surface-variant">
              {item.sponsor && (
                <p>
                  <span className="font-semibold">Sponsor:</span>{' '}
                  <span dangerouslySetInnerHTML={renderHtml(item.sponsor)} />
                </p>
              )}
              {item.period && (
                <p>
                  <span className="font-semibold">Period:</span>{' '}
                  <span dangerouslySetInnerHTML={renderHtml(item.period)} />
                </p>
              )}
              {item.amount && (
                <p>
                  <span className="font-semibold">Amount:</span>{' '}
                  <span dangerouslySetInnerHTML={renderHtml(item.amount)} />
                </p>
              )}
            </div>
            {item.description && (
              <p
                className="mt-3 text-sm leading-relaxed text-on-surface-variant"
                dangerouslySetInnerHTML={renderHtml(item.description)}
              />
            )}
          </article>
        ))}
      </div>
    );
  };

  return (
    <section className="bg-surface min-h-full">
      <PageHero
        title="Funding Sources"
        intro="This page lists externally funded projects, including current and completed support records."
        sectionLabel="Project Grants"
      />
      <div className="mx-auto max-w-screen-2xl px-8 pb-20 pt-12">
        {loading ? (
          <p className="text-on-surface-variant">Loading...</p>
        ) : error ? (
          <div className="rounded-md bg-error-container px-4 py-3 text-sm text-on-error-container">
            {error}
          </div>
        ) : (
          <div className="space-y-12">
            <section>
              <h2 className="mb-5 text-3xl font-semibold text-primary">
                On-going Projects:
              </h2>
              {renderList(ongoingProjects)}
            </section>

            <section>
              <h2 className="mb-5 text-3xl font-semibold text-primary">
                Completed Projects:
              </h2>
              {renderList(completedProjects)}
            </section>
          </div>
        )}
      </div>
    </section>
  );
};

export default FundingSources;
